/* eslint-disable no-param-reassign */
import {
  ADD_SERVICE_PART,
  ADD_SERVICE_PART_FROM_INVENTORY,
  ADD_SERVICE_PARTS,
  CHANGE_SERVICE_PART,
  REMOVE_SERVICE_PART,
  REMOVE_SERVICE_PARTS,
  RESET_SERVICE_PARTS,
  ServicePartActions,
} from '@models';
import { DispachedServicePart, InitialServicePart, Service } from '@services';
import { Utils } from '@utils';
import { makeAutoObservable, runInAction } from 'mobx';

/**
 * One job's worth of services and their parts.
 *
 * `serviceStore` already satisfies this shape with its own `selected*` members, so a component that
 * takes an `IServiceBasket` works unchanged against the store - which is what keeps every existing
 * call site of OrderServiceSelect and ServiceDetailsCard working while the booking drawer supplies
 * its own baskets instead.
 */
export interface IServiceBasket {
  selectedServices: Service[];
  selectedServiceParts: Map<number, DispachedServicePart[]>;
  tenantVat: number;
  addService: (id: number) => void;
  removeService: (id: number, keepAtLeastOne?: boolean) => void;
  clearSelectedServices: () => void;
  updateServiceField: (id: number, field: string, value: any) => void;
  dispatchSelectedServiceParts: (action: ServicePartActions) => void;
  addPartsFromInventoryBulk: (
    createdItems: any[],
    parsedItems: any[] | undefined,
    variant?: 'selected' | 'additional' | 'revision',
    defaultServiceId?: number
  ) => void;
  readonly pricingSummary: {
    labourPrice: number;
    partsPrice: number;
    /** Cost x quantity of the parts, ex VAT. */
    partsCost: number;
    /** partsPrice less partsCost: the profit made on parts, ex VAT. */
    partsProfit: number;
    subTotal: number;
    vatAmount: number;
    totalPrice: number;
  };
}

/**
 * A basket that stands on its own, for screens that must hold several unsaved jobs at once - the
 * booking drawer's build mode keeps one per job it is about to create.
 *
 * The service catalogue itself is not duplicated: the lookup is handed in and stays owned by
 * serviceStore, so the search results every basket picks from are the same ones.
 */
export class ServiceBasket implements IServiceBasket {
  selectedServices: Service[] = [];
  selectedServiceParts: Map<number, DispachedServicePart[]> = new Map();
  tenantVat: number = 0;

  /** Shared catalogue owned by serviceStore - read only, never mutated here. */
  private lookup: Map<number, Service>;

  constructor(lookup: Map<number, Service>, tenantVat: number = 0) {
    this.lookup = lookup;
    this.tenantVat = tenantVat;
    makeAutoObservable(this, { }, { autoBind: true });
  }

  setTenantVat = (vat: number) => {
    runInAction(() => {
      this.tenantVat = vat;
    });
  };

  /**
   * Seeds a basket straight from a template's service, snapshotting price and duration the way the
   * API does when it expands a template itself.
   */
  seedFromServices = (services: Service[]) => {
    runInAction(() => {
      this.selectedServices = services.map((service) => ({ ...service }));
      this.selectedServiceParts.clear();
      services.forEach((service) => {
        this.selectedServiceParts.set(
          service.id,
          (service.serviceParts ?? []).map((part) => ({ ...part, required: false }))
        );
      });
    });
  };

  addService = (id: number) => {
    const service = this.lookup.get(id);
    if (!service) return;
    runInAction(() => {
      this.selectedServices = [...this.selectedServices, service];
    });
    this.dispatchSelectedServiceParts({
      type: ADD_SERVICE_PARTS,
      serviceId: id,
      serviceParts: (service.serviceParts ?? []).map((part) => ({ ...part, required: false })),
    });
  };

  removeService = (id: number, keepAtLeastOne = false) => {
    if (keepAtLeastOne && this.selectedServices.length === 1) return;
    runInAction(() => {
      this.selectedServices = this.selectedServices.filter((x) => x.id !== id);
    });
    this.dispatchSelectedServiceParts({ type: REMOVE_SERVICE_PARTS, serviceId: id });
  };

  clearSelectedServices = () => {
    runInAction(() => {
      this.selectedServices = [];
    });
    this.dispatchSelectedServiceParts({ type: RESET_SERVICE_PARTS });
  };

  updateServiceField = (id: number, field: string, value: any) => {
    runInAction(() => {
      this.selectedServices = this.selectedServices.map((s) =>
        s.id === id ? { ...s, [field]: value } : s
      );
    });
  };

  addPartsFromInventoryBulk = (
    createdItems: any[],
    parsedItems: any[] | undefined,
    _variant: 'selected' | 'additional' | 'revision' = 'selected',
    defaultServiceId?: number
  ) => {
    // A basket has no additional/revision sets - there is only ever the one job in it - so the
    // variant is accepted for interface compatibility and ignored.
    createdItems.forEach((item, idx) => {
      const parsedItem = parsedItems?.[idx];
      const targetServiceId = parsedItem?.serviceId ?? defaultServiceId;
      if (targetServiceId === undefined || targetServiceId === null) return;
      const qty = parsedItem?.quantity ?? item.quantity ?? 1;
      const costPrice = parsedItem?.costPrice ?? item.costPrice;
      const sellPrice = parsedItem?.sellPrice ?? item.sellPrice;
      this.dispatchSelectedServiceParts({
        type: ADD_SERVICE_PART_FROM_INVENTORY,
        serviceId: targetServiceId,
        servicePart: {
          name: item.name,
          number: item.partNumber,
          reference: item.reference,
          costPrice,
          price: sellPrice,
          quantity: qty,
          margin: sellPrice > 0 ? ((sellPrice - costPrice) / sellPrice) * 100 : 0,
          type: item.categoryName || 'Part',
          inventoryId: item.id,
          required: false,
        },
      });
    });
  };

  /** Same reducer as serviceStore's, against this basket's own parts map. */
  dispatchSelectedServiceParts = (action: ServicePartActions) => {
    const map = this.selectedServiceParts;

    switch (action.type) {
      case ADD_SERVICE_PART:
        runInAction(() => {
          const parts = map.get(action.serviceId!) || [];
          map.set(action.serviceId!, [
            ...parts,
            { ...InitialServicePart, serviceId: action.serviceId },
          ]);
        });
        return;
      case ADD_SERVICE_PART_FROM_INVENTORY:
        runInAction(() => {
          const parts = map.get(action.serviceId) || [];
          const incomingInventoryId = action.servicePart?.inventoryId;
          const existingIdx = incomingInventoryId
            ? parts.findIndex((p) => p.inventoryId === incomingInventoryId)
            : -1;
          // Picking the same stock item twice tops up the line rather than adding a duplicate.
          if (existingIdx >= 0) {
            const existing = parts[existingIdx];
            const incomingQty = action.servicePart?.quantity ?? 1;
            const next = [...parts];
            next[existingIdx] = { ...existing, quantity: (existing.quantity ?? 0) + incomingQty };
            map.set(action.serviceId, next);
          } else {
            map.set(action.serviceId, [
              ...parts,
              { ...action.servicePart, serviceId: action.serviceId },
            ]);
          }
        });
        return;
      case ADD_SERVICE_PARTS:
        runInAction(() => {
          map.set(action.serviceId!, action.serviceParts);
        });
        return;
      case REMOVE_SERVICE_PART:
        runInAction(() => {
          const parts = [...(map.get(action.serviceId!) || [])];
          parts.splice(action.index, 1);
          map.set(action.serviceId!, parts);
        });
        return;
      case REMOVE_SERVICE_PARTS:
        runInAction(() => {
          map.delete(action.serviceId!);
        });
        return;
      case CHANGE_SERVICE_PART:
        runInAction(() => {
          const parts = [...(map.get(action.serviceId!) || [])];
          parts[action.index] = { ...parts[action.index], ...action.servicePart };
          map.set(action.serviceId!, parts);
        });
        return;
      case RESET_SERVICE_PARTS:
        runInAction(() => {
          map.clear();
        });
        return;
      default:
    }
  };

  /** Identical arithmetic to serviceStore's, so a basket's totals agree with the job card's. */
  get pricingSummary() {
    const labourPrice = this.selectedServices.reduce(
      (acc, curr) => acc + Utils.calculateLabourPrice({ ...curr }),
      0
    );

    const allParts: DispachedServicePart[] = Array.from(this.selectedServiceParts.values()).flat();
    const partsPrice = Utils.calculatePartsPrice(allParts);
    const subTotal = labourPrice + partsPrice;

    const serviceVat = this.selectedServices.reduce(
      (acc, curr) =>
        acc +
        (curr.isVat ? Utils.calculateVat({ ...curr, vat: curr.isVat ? this.tenantVat : 0 }) : 0),
      0
    );

    const partsVat = allParts.reduce(
      (acc, curr) =>
        acc + Utils.calculateVat({ ...curr, duration: curr.quantity, vat: this.tenantVat }),
      0
    );

    const vatAmount = serviceVat + partsVat;

    const partsCost = Utils.calculatePartsCost(allParts);
    const partsProfit = Utils.roundToTwoDecimals(partsPrice - partsCost);

    return {
      labourPrice,
      partsPrice,
      partsCost,
      partsProfit,
      subTotal,
      vatAmount,
      totalPrice: subTotal + vatAmount,
    };
  }
}
